// ─── L'anagrafica e il borsellino ────────────────────────────────────────────
//
// 🔑 L'IDENTITÀ È `id`, E NIENT'ALTRO. Nel sistema di oggi il socio È il suo
// telefono (`local_key = 'phone:39…'` per 2.798 su 2.826): chi cambia numero
// cambia chiave, e ciò che era legato a lui si scollega. Qui il telefono è un
// attributo unico fra i vivi, e basta.
// ⛔ `external_ref` — la chiave di origine — non compare in nessuno schema di
// questo file, e non è una dimenticanza: esiste solo perché una seconda corsa
// del travaso ritrovi la stessa persona. Il giorno in cui una schermata lo
// mostra, qualcuno comincerà a usarlo per riconoscere qualcuno.
//
// 💶 Il denaro sta in CENTESIMI interi, sempre. La virgola la mette solo
// `formatCents`, all'ultimo momento, davanti agli occhi di chi legge.
import { z } from 'zod';
import { dataSchema } from './circolo.js';
import { chiaveIdempotenzaSchema } from './money.js';
import { metodoDiCassaSchema } from './cassa.js';
import { tipoBookingSchema } from './calendario.js';
// ── Il socio ────────────────────────────────────────────────────────────────
/** Il vincolo del database, lo stesso della colonna `phone_e164`. */
const TELEFONO = /^\+[1-9][0-9]{6,17}$/;
/**
 * Il telefono in forma internazionale.
 *
 * ⚖️ La forma si impone in SCRITTURA, non in lettura: è il vincolo del
 * database (`^\+[1-9][0-9]{6,17}$`) portato fin qui, così chi digita legge una
 * frase in italiano invece di una violazione di `check`. E senza questa
 * severità l'unicità confronterebbe «333 123 4567» con «+393331234567» come
 * fossero due persone — che è esattamente il doppione che il travaso conta.
 */
export const telefonoSchema = z
    .string()
    // 📞 Voce 25: il numero si scrive come viene, e si normalizza PRIMA del
    // controllo, con la stessa funzione del travaso e del bot (⇒ `normalizzaTelefono`).
    // Quando non sa, si lascia com'è scritto: il controllo lo rifiuta in italiano.
    .transform((scritto) => normalizzaTelefono(scritto) ?? scritto.trim())
    .pipe(z
    .string()
    .regex(TELEFONO, 'Il telefono non sembra un numero: controlla che ci siano solo cifre, almeno sei.'));
/**
 * Il livello di gioco, scritto a mano dalla segreteria.
 *
 * ⛔ Non arriva da nessuna autovalutazione: quella è fuori perimetro (§ README.1.2).
 * `numeric(3,1)` in colonna ⇒ un decimale, e niente di più fine.
 */
export const livelloSchema = z.number().min(0).max(99.9);
export const socioSchema = z.object({
    id: z.uuid(),
    firstName: z.string(),
    lastName: z.string(),
    /** Generato dal DATABASE, non qui: due posti che compongono il nome lo compongono diverso. */
    displayName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    gender: z.enum(['M', 'F']).nullable(),
    /**
     * 🙋 16/10/2026: il sesso l'ha scritto il bot (dai tipi di partita scelti dal
     * socio) e la Segreteria non l'ha ancora guardato: «🟡 Scritto dal bot: da
     * controllare». Lo spegne «✓ Controllato», o un sesso scritto dalla Segreteria.
     */
    genderToCheck: z.boolean().default(false),
    birthDate: dataSchema.nullable(),
    level: z.number().nullable(),
    levelNote: z.string().nullable(),
    notes: z.string().nullable(),
    /** Dati morbidi su cui nessuno interroga: giorni e fasce preferite, tipo di partita. */
    preferences: z.record(z.string(), z.unknown()),
    active: z.boolean(),
    /** Valorizzata ⇒ archiviato: la riga resta, esce dagli elenchi e dalle unicità. */
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    /**
     * 🆕 28/09/2026 (Partite Aperte): almeno una prova del test del livello
     * consegnata. Con `level` dice se per il bot il test è «fatto» (scelta 1A).
     */
    hasAssessment: z.boolean().default(false),
    /**
     * 🆕 28/09/2026 (registrazione dall'assistente): la scheda è nata dal socio,
     * sul bot, col numero verificato da Telegram — non dalla Segreteria.
     */
    fromAssistant: z.boolean().default(false),
});
/**
 * 🗑️ La storia di un socio al circolo, per decidere se la scheda si può cancellare (28/09/2026,
 * mockup «Correggi e cancella»: *«1A 2A 3A»*).
 *
 * ⚖️ Scelta 2A: si cancella solo una scheda **senza storia** — un doppione, un errore, una prova,
 * chi si è registrato dal bot e non ha mai giocato. Partite, pagamenti, movimenti, Crediti e
 * acconti restano per la contabilità del circolo: chi li ha si archivia.
 */
export const storiaDelSocioSchema = z.object({
    /** Partite in cui è in campo o che ha organizzato, passate e future. */
    matches: z.number().int(),
    payments: z.number().int(),
    walletMovements: z.number().int(),
    /** Crediti da organizzatore. */
    credits: z.number().int(),
    /** Gli acconti della sera di prima (30/09/2026: non ne nascono più), che tengono la scheda. */
    deposits: z.number().int(),
    /** Corsi: iscritto o in lista d'attesa. */
    courses: z.number().int(),
    noShows: z.number().int(),
    /** Tutto a zero ⇒ si può cancellare. */
    deletable: z.boolean(),
});
/**
 * 🧪 «Azzera il livello», solo per le prove (14/10/2026, mockup «Azzera il livello», *«vai»*).
 * 🗣️ Maurizio Aprea: *«Azzeriamo il mio livello di gioco. Con un bottone, solo sul mio nome»* ·
 * *«solo nel gestionale di test»*. `available` lo decide il gestionale: il titolare, la scheda di
 * Maurizio Aprea, e il bot che si è presentato «di prova».
 */
export const azzeraLivelloSchema = z.object({ available: z.boolean() });
/**
 * 🗑️ Il titolare cancella anche un socio CON una storia (29/09/2026, mockup «Cancellare un socio»:
 * *«1 A - 2 Solo il titolare - 3 si»*). Via la persona, restano i conti a nome «Socio cancellato».
 * ⭐ Scelta 3: la conferma è il nome del socio scritto per intero, e il server lo ricontrolla.
 */
export const cancellazioneConStoriaSchema = z.object({
    confirmName: z.string().trim().min(1).max(200),
});
export const esitoCancellazioneConStoriaSchema = z.object({
    erased: z.literal(true),
    /** Quanto c'era nel borsellino, azzerato con un movimento «Cancellazione della scheda». */
    walletZeroedCents: z.number().int(),
});
/** Il nome che una scheda cancellata porta nelle partite, in Cassa e nel registro. */
export const NOME_DEL_SOCIO_CANCELLATO = 'Socio cancellato';
/** Il nome scritto per confermare è quello del socio: spazi e maiuscole non contano. */
export function stessoNome(scritto, nome) {
    const pulito = (s) => s.trim().replace(/\s+/g, ' ').toLocaleLowerCase('it');
    return pulito(scritto) !== '' && pulito(scritto) === pulito(nome);
}
/**
 * La riga dell'elenco: **meno** campi del socio intero, e di proposito.
 *
 * ⚖️ Su ~2.800 soci la lista si sfoglia mentre si digita: ogni campo in più è
 * peso che attraversa la rete a ogni battuta. La scheda piena si chiede quando
 * qualcuno la apre.
 */
export const socioInElencoSchema = z.object({
    id: z.uuid(),
    displayName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    level: z.number().nullable(),
    active: z.boolean(),
    archived: z.boolean(),
    /** 💶 `null` = nessun movimento, che NON è «saldo zero verificato». */
    walletBalanceCents: z.number().int().nullable(),
    /** 🆕 Iscritto dall'assistente: nell'elenco porta l'etichetta «🆕 dall'assistente». */
    fromAssistant: z.boolean().default(false),
    /**
     * 🆕 A7: quanti account Telegram sono collegati alla scheda. Più di uno
     * porta l'etichetta «2 account Telegram» (scelta 3A): decide la Segreteria.
     */
    telegramAccounts: z.number().int().default(0),
    /** 🙋 16/10/2026: il sesso scritto dal bot, da controllare (l'etichetta «🟡 sesso da controllare»). */
    genderToCheck: z.boolean().default(false),
    /**
     * 🙋 19/10/2026 (mockup «Soci col sesso», A): la colonna Sesso dell'elenco, al posto dell'email.
     * L'email resta nella riga per «Esporta» e nella scheda (abbina la mail della tessera).
     */
    gender: z.enum(['M', 'F']).nullable().default(null),
});
export const elencoSociSchema = z.object({
    items: z.array(socioInElencoSchema),
    /**
     * Quanti ce ne sono in tutto per questa ricerca.
     *
     * ⚠️ `null` quando non si è contato — e si conta solo alla prima pagina:
     * un `count(*)` su ogni battuta di tastiera è la cosa che fa sembrare lenta
     * una ricerca che è veloce.
     */
    total: z.number().int().nullable(),
    /** L'ultimo elemento di questa pagina, da rimandare indietro per la prossima. */
    nextCursor: z.string().nullable(),
});
/**
 * 🚨 `z.coerce` sui numeri della querystring: da lì arriva SEMPRE una stringa,
 * e uno schema che si aspetta un numero rifiuta `?limit=50` senza dire perché.
 */
export const domandaSociSchema = z.object({
    q: z.string().trim().max(120).optional(),
    /** `true` = solo gli attivi (il caso normale), `false` = solo i disattivati. */
    active: z.stringbool().optional(),
    /** Gli archiviati stanno FUORI se non si chiedono: sono usciti dal circolo. */
    archived: z.stringbool().default(false),
    /** 🆕 `true` = solo le schede nate dall'assistente (la spunta dell'elenco). */
    fromAssistant: z.stringbool().optional(),
    /** 🙋 16/10/2026: `true` = solo le schede col sesso scritto dal bot e da controllare. */
    genderToCheck: z.stringbool().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
    cursor: z.string().max(200).optional(),
});
const nomeSchema = z.string().trim().min(1, 'Serve il nome').max(80);
export const creaSocioSchema = z.object({
    firstName: nomeSchema,
    lastName: z.string().trim().min(1, 'Serve il cognome').max(80),
    email: z.email('L’indirizzo email non è scritto bene').max(200).nullable().default(null),
    phone: telefonoSchema.nullable().default(null),
    gender: z.enum(['M', 'F']).nullable().default(null),
    birthDate: dataSchema.nullable().default(null),
    level: livelloSchema.nullable().default(null),
    levelNote: z.string().trim().max(400).nullable().default(null),
    notes: z.string().trim().max(2000).nullable().default(null),
});
/**
 * ⛔ Scritto per esteso, NON con `.partial()`.
 *
 * ⚖️ `.partial()` renderebbe opzionale anche ciò che non deve esserlo, e — la
 * parte che costa — non distingue «non tocco questo campo» da «voglio
 * svuotarlo»: `null` è un valore legittimo per email, telefono e livello.
 * Scrivendolo a mano, `undefined` vuol dire «lascia com'è» e `null` vuol dire
 * «cancella», e le due cose sono leggibili.
 */
export const modificaSocioSchema = z.object({
    firstName: nomeSchema.optional(),
    lastName: z.string().trim().min(1).max(80).optional(),
    email: z.email('L’indirizzo email non è scritto bene').max(200).nullable().optional(),
    phone: telefonoSchema.nullable().optional(),
    gender: z.enum(['M', 'F']).nullable().optional(),
    birthDate: dataSchema.nullable().optional(),
    level: livelloSchema.nullable().optional(),
    levelNote: z.string().trim().max(400).nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    active: z.boolean().optional(),
});
// ── Il borsellino ───────────────────────────────────────────────────────────
export const VERSI_BORSELLINO = ['recharge', 'charge', 'refund'];
/** Come si dicono in italiano, in un posto solo. */
export const ETICHETTE_VERSO = {
    recharge: 'Ricarica',
    charge: 'Uscita',
    refund: 'Rimborso',
};
/** `+1` mette dentro, `-1` toglie. È la stessa regola della vista `member_wallet_balance`. */
export function segnoDelVerso(v) {
    return v === 'charge' ? -1 : 1;
}
export const movimentoBorsellinoSchema = z.object({
    id: z.uuid(),
    direction: z.enum(VERSI_BORSELLINO),
    /** ⭐ SEMPRE POSITIVO: il verso lo dà `direction`. Due verità sullo stesso segno divergono. */
    amountCents: z.number().int().positive(),
    reason: z.string().nullable(),
    /** L'incasso da cui nasce, quando ce n'è uno (Fase 6). */
    paymentId: z.uuid().nullable(),
    /**
     * ⭐ Valorizzato ⇒ questa riga **È lo storno** di quella, e non si storna a
     * sua volta. 🗣️ *«Fallo uguale nel borsellino»* (18/09/2026): è la gemella di
     * `voidsPaymentId` in cassa, e nasce dallo stesso difetto — prima il legame
     * viveva solo in una frase del motivo, e uno storno si poteva stornare
     * all'infinito.
     */
    reversesTransactionId: z.uuid().nullable(),
    /** Valorizzato ⇒ questa riga **è stata stornata** da quella. */
    reversedByTransactionId: z.uuid().nullable(),
    recordedAt: z.string(),
    recordedByEmail: z.string().nullable(),
});
export const borsellinoSchema = z.object({
    memberId: z.uuid(),
    /**
     * 💶 Viene dalla VISTA, non da una colonna: il saldo si calcola dai
     * movimenti. Una colonna che qualcuno aggiorna è una colonna che prima o poi
     * mente, e quando mente nessuno se ne accorge perché è l'unica cosa che si
     * guarda.
     */
    balanceCents: z.number().int(),
    transactions: z.array(movimentoBorsellinoSchema),
});
export const ricaricaSchema = z.object({
    amountCents: z.number().int().positive('La ricarica dev’essere positiva'),
    reason: z.string().trim().max(200).nullable().default(null),
    /**
     * 💶 CON CHE MEZZO IL DENARO È PASSATO DAL CASSETTO — e questa riga è quella
     * che fa quadrare la giornata (Fase 6).
     *
     * ⚖️ Una ricarica da 50,00 € in contanti mette 50,00 € nel cassetto: se
     * restasse solo un movimento del borsellino, a fine serata la segreteria
     * conterebbe 50,00 € che il riepilogo non sa spiegare. ⇒ Con un mezzo qui, la
     * ricarica genera **anche** la sua riga di cassa, nella stessa transazione.
     * ⛔ `null` = rettifica: un aggiustamento che NON tocca il cassetto (un bonus,
     *    la correzione di un travaso). È lecito, ma è una scelta da fare apposta —
     *    per questo il valore non ha un valore predefinito comodo.
     */
    method: metodoDiCassaSchema.nullable().default(null),
    idempotencyKey: chiaveIdempotenzaSchema,
});
/**
 * Togliere denaro dal borsellino: il socio se lo riprende.
 *
 * 🔄 **Il § 02.5 la chiamava `/refund`, e il nome era ambiguo.** Nella vista
 * del saldo `refund` è un verso che **aggiunge** (Fase 6: un incasso col
 * borsellino che viene stornato ci rimette dentro i soldi). ⇒ Chiamare
 * `/refund` la rotta che TOGLIE avrebbe fatto sì che la stessa parola volesse
 * dire due cose opposte a due metri di distanza.
 * 📌 Un nome che descrive il gesto da una parte e il suo contrario dall'altra
 *    non è un dettaglio di stile: è un errore che aspetta.
 */
export const prelievoSchema = z.object({
    amountCents: z.number().int().positive('L’uscita dev’essere positiva'),
    reason: z.string().trim().max(200).nullable().default(null),
    /**
     * 💶 Come sopra, al contrario: restituire 50,00 € in contanti li TOGLIE dal
     * cassetto, e in cassa è una riga negativa. Senza, il riepilogo direbbe che
     * quei contanti ci sono ancora.
     */
    method: metodoDiCassaSchema.nullable().default(null),
    idempotencyKey: chiaveIdempotenzaSchema,
});
/**
 * Lo storno di un movimento: ne scrive uno **uguale e contrario**.
 *
 * ⛔ L'importo NON si passa: si prende dal movimento che si sta stornando. Se
 * lo scrivesse chi chiama, uno storno potrebbe valere più dell'originale — e
 * il borsellino guadagnerebbe denaro da un gesto che si chiama «annulla».
 */
export const stornoBorsellinoSchema = z.object({
    transactionId: z.uuid(),
    reason: z.string().trim().max(200).nullable().default(null),
});
// ── La scheda del socio ─────────────────────────────────────────────────────
export const rigaAttivitaSchema = z.object({
    bookingId: z.uuid(),
    date: dataSchema,
    time: z.string(),
    durationMinutes: z.number().int(),
    courtName: z.string(),
    /* ⚖️ Stretto a `tipoBookingSchema` e non più `z.string()`: la scheda del socio
       scrive il TIPO al posto del titolo (17/09/2026), e per scriverlo in italiano
       serve sapere che è uno dei cinque. Una stringa qualunque non lo garantiva. */
    type: tipoBookingSchema,
    title: z.string().nullable(),
    status: z.string(),
    /** Quanto gli è stato messo a carico su quella prenotazione. */
    amountDueCents: z.number().int().nullable(),
});
export const rigaIncassoSchema = z.object({
    id: z.uuid(),
    businessDate: dataSchema,
    method: z.string(),
    amountCents: z.number().int(),
    status: z.string(),
    recordedAt: z.string(),
});
export const attivitaSocioSchema = z.object({
    bookings: z.array(rigaAttivitaSchema),
    payments: z.array(rigaIncassoSchema),
});
export const domandaAttivitaSchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
// ── L'export ────────────────────────────────────────────────────────────────
export const domandaExportSchema = z.object({
    format: z.enum(['csv', 'xlsx']).default('csv'),
    q: z.string().trim().max(120).optional(),
    active: z.stringbool().optional(),
    archived: z.stringbool().default(false),
    /** 🆕 La stessa spunta dell'elenco: si esporta quello che si vede. */
    fromAssistant: z.stringbool().optional(),
    genderToCheck: z.stringbool().optional(),
});
// ── L'import dalla rubrica Google ───────────────────────────────────────────
export const contattoTrovatoSchema = z.object({
    displayName: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    /**
     * Cosa succederebbe a questo contatto:
     *   · `nuovo`     — non c'è nessun socio con questo telefono o questa email;
     *   · `aggiorna`  — c'è, e qualche campo cambierebbe;
     *   · `uguale`    — c'è, e non cambierebbe niente;
     *   · `scartato`  — manca il nome, o il telefono non è normalizzabile.
     */
    esito: z.enum(['nuovo', 'aggiorna', 'uguale', 'scartato']),
    /** Il socio che questo contatto tocca, quando ce n'è uno. */
    memberId: z.uuid().nullable(),
    perché: z.string().nullable(),
});
/**
 * 🚨 L'ANTEPRIMA VIENE PRIMA DELLA SCRITTURA, ED È IL PUNTO DELLA ROTTA.
 *
 * ⚖️ Una rubrica personale contiene il dentista e il meccanico. Riversarla
 * nell'anagrafica del circolo senza guardare vuol dire trovarsi centinaia di
 * non-soci il giorno dopo, e nessuno saprà più quali righe erano lì prima.
 * ⇒ `apply: false` (il difetto) **legge e basta**; `apply: true` scrive, e
 *   scrive SOLO i contatti che chi ha guardato ha scelto.
 */
export const domandaImportSchema = z.object({
    apply: z.boolean().default(false),
    /** Gli indirizzi da scrivere davvero. Vuoto con `apply: true` = non si scrive niente. */
    scelti: z.array(z.string()).default([]),
});
export const esitoImportSchema = z.object({
    /** `false` ⇒ è stata solo un'anteprima: niente è stato scritto. */
    applicato: z.boolean(),
    contatti: z.array(contattoTrovatoSchema),
    creati: z.number().int(),
    aggiornati: z.number().int(),
});
// ── Le due funzioni pure che il travaso e l'API DEVONO condividere ──────────
//
// 🚨 Stanno qui, e non in ciascuno dei due, perché normalizzano la chiave su
// cui poggia l'unicità dei soci. Due copie che divergono di un carattere fanno
// entrare la stessa persona due volte a seconda della porta da cui arriva — il
// travaso o l'import della rubrica — e nessuno se ne accorge finché non ci
// sono due «Mario Rossi» con lo stesso numero scritto in due modi.
/**
 * Porta un numero alla forma `+39…`.
 *
 * 🔄 **È L'UNICA regola del telefono**, e dal 25/09/2026 la usano anche i
 * moduli della scheda (voce 25) e l'importazione (voce 26). La voce 25 ne
 * aveva scritta una seconda, `ripulisciTelefono`, che divergeva su due casi (il
 * fisso senza prefisso, e un numero estero scritto senza «+»). È stata tolta:
 * due regole sono la stessa persona che entra due volte da due porte.
 *
 * 🚨 Torna `null` quando NON SA, e non prova a indovinare. Un numero
 * normalizzato male è peggio di un numero mancante: collide con quello di
 * un'altra persona sul vincolo di unicità, e le due righe diventano una.
 *
 * 📏 Misurato sulla sorgente il 15/09/2026 su 2.826 soci: 2.776 già `+39`
 * puliti, 45 con un altro prefisso internazionale, 4 vuoti, 1 di altra forma.
 * ⇒ Il caso da non sbagliare non è la massa: è quell'uno.
 */
export function normalizzaTelefono(grezzo) {
    if (grezzo == null)
        return null;
    // Spazi, punti, trattini, parentesi e lo spazio unificatore che arriva
    // incollato dai fogli di calcolo.
    let pulito = String(grezzo).replace(/[\s\u00a0.\-()/]/g, '');
    if (pulito === '')
        return null;
    // 📏 **Il prefisso DOPPIO di Matchpoint** (voce 26, misurato il 25/09/2026 sul
    // suo export: 12 righe su 1.132). Scrive «+39 » davanti a un numero che il
    // prefisso ce l'ha già: «+39 +393331234567», «+39 +41791234567». Il numero
    // vero è quello dopo l'ULTIMO «+».
    const ultimoPiu = pulito.lastIndexOf('+');
    if (ultimoPiu > 0)
        pulito = pulito.slice(ultimoPiu);
    let cifre;
    if (pulito.startsWith('+')) {
        cifre = pulito.slice(1);
    }
    else if (pulito.startsWith('00')) {
        cifre = pulito.slice(2);
    }
    else if (/^3[0-9]{8,9}$/.test(pulito)) {
        // Un cellulare italiano scritto senza prefisso: è il caso di gran lunga
        // più comune fra quelli da sistemare — perché il circolo è in Italia e i
        // fissi non cominciano per 3.
        cifre = `39${pulito}`;
    }
    else if (/^0[0-9]{5,10}$/.test(pulito)) {
        // 📞 Voce 25: un fisso italiano senza prefisso («02 1234 5678»). In Italia
        // i fissi cominciano per 0, e lo 0 resta anche col prefisso: +39 02….
        cifre = `39${pulito}`;
    }
    else {
        // Senza «+», senza «00», e non comincia né per 3 né per 0: è un numero
        // che il prefisso ce l'ha già, scritto senza «+» («393331234567»,
        // «41791234567»). Aggiungere +39 qui lo sbaglierebbe.
        cifre = pulito;
    }
    if (!/^[1-9][0-9]{6,17}$/.test(cifre))
        return null;
    return `+${cifre}`;
}
/**
 * Spacca un nome in nome e cognome.
 *
 * ⚠️ La sorgente porta `firstName` e `surname` già separati per tutti i 2.826
 * soci: questa funzione è il RIPIEGO per quando uno dei due manca, non la
 * strada principale. Un cognome composto («De Luca») resta intero perché si
 * prende solo la PRIMA parola come nome.
 */
export function spaccaNome(intero) {
    const pezzi = String(intero ?? '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (pezzi.length === 0)
        return { nome: '', cognome: '' };
    if (pezzi.length === 1)
        return { nome: pezzi[0], cognome: '' };
    return { nome: pezzi[0], cognome: pezzi.slice(1).join(' ') };
}
/**
 * ⭐⭐ **IL NOME ACCORCIATO PER IL CALENDARIO** — 17/09/2026, richiesta sua.
 *
 * 🗣️ *«possiamo introdurre la regola che mettiamo il nome e il cognome puntato
 *   [...] è il cognome puntato, non il nome. E direi di farlo quando lo spazio
 *   non basta»*.
 *
 * `Lidia` + `Comes` → **`Lidia C.`**
 *
 * ⚠️ **Gli è stato detto prima di farlo, e va ricordato qui**: puntando il
 * COGNOME si accorcia la metà che identifica di più — i nomi si ripetono molto
 * più dei cognomi. ⇒ Per questo esiste `accorciaElenco` qui sotto, che rimette
 * per esteso i nomi che si confonderebbero. Usare questa funzione da sola, su
 * un elenco, è il modo di reintrodurre il difetto.
 *
 * ⛔ Senza cognome non si inventa un puntino: un ospite registrato come
 * «Giovanni» resta «Giovanni». Un nome vuoto resta vuoto.
 */
export function nomeAccorciato(nome, cognome) {
    const n = nome.trim();
    const c = cognome.trim();
    if (n === '' || c === '')
        return n === '' ? c : n;
    // ⚠️ Il primo CARATTERE, non la prima lettera dell'ultima parola: «De Luca»
    // diventa «D.», che è quello che si scriverebbe a mano.
    return `${n} ${c[0]?.toUpperCase() ?? ''}.`;
}
/**
 * ⭐⭐ **ACCORCIA UN ELENCO, MA NON FINO A CONFONDERE DUE PERSONE.**
 *
 * ⚖️ È la guardia promessa quando si è scelto di puntare il cognome: due
 * «Marco A.» dentro la stessa prenotazione sono esattamente il difetto che
 * l'accorciatura doveva curare — un blocchetto che non dice chi c'è — solo
 * spostato di un passo.
 * ⇒ Chi si scontra torna per esteso; gli altri restano corti.
 *
 * 📌 *Non conta quante coppie ambigue esistano nel circolo: conta se capitano
 *    nello stesso riquadro. È lì che si guarda, ed è lì che si controlla.*
 *
 * ⚠️ Il confronto è su ciò che si VEDE (maiuscole e spazi normalizzati): due
 * scritte identiche a schermo sono ambigue anche se in colonna differiscono.
 */
export function accorciaElenco(voci) {
    const quanti = new Map();
    for (const v of voci) {
        const chiave = v.corto.trim().toLowerCase();
        quanti.set(chiave, (quanti.get(chiave) ?? 0) + 1);
    }
    return voci.map((v) => ((quanti.get(v.corto.trim().toLowerCase()) ?? 0) > 1 ? v.intero : v.corto));
}
/**
 * ⭐⭐ **CHI È GIÀ IN CAMPO, come insieme di identificativi.**
 *
 * 🗣️ Nasce il 17/09/2026 da una sua segnalazione: in una lezione con dentro
 * Laura Aprea e Marco Aprea, cercando «aprea» la scheda **li riproponeva tutti
 * e due**. Cliccarli non aggiungeva niente — il server risponde `409
 * SOCIO_GIA_IN_CAMPO` — ma la domanda era già stata fatta, e una domanda a cui
 * esiste una sola risposta sbagliata non andava fatta.
 *
 * ⚖️ **Gli OSPITI restano fuori di proposito.** Un ospite non è un'identità: è
 * un nome scritto a mano, e due persone possono chiamarsi uguale. Il server la
 * pensa allo stesso modo (`booking_players_socio_unico` è parziale, `where
 * member_id is not null`) ⇒ proibire due «Mario» sarebbe più severo della
 * regola vera, e si tratterebbe di una regola inventata qui.
 *
 * 🔒 Sta in `shared` perché i posti da guardare sono **due** e devono dire la
 * stessa cosa: la scheda di una prenotazione che esiste (i suoi `players`) e
 * quella di una che si sta creando (i posti ancora solo nel browser).
 */
export function sociGiaInCampo(posti) {
    return new Set(posti.map((p) => p.memberId).filter((x) => x !== null));
}
// ── 🪪 Il nome che vedono gli altri giocatori (13/10/2026) ───────────────────
/**
 * ⭐⭐ **IL COGNOME PUNTATO FRA I GIOCATORI** — 13/10/2026, mockup «Cognome
 * puntato» approvato: *«1A 2A»*.
 *
 * 🗣️ *«quando un utente si registra, poi si vede solo il nome e il cognome
 *   puntato a tutti i giocatori dentro il chatbot. Solo se un giocatore poi
 *   decide di dare il consenso totale, allora tutti vedono il suo nome e
 *   cognome per intero.»*
 *
 * `Luca` + `Bianchi` → **`Luca B.`**; col consenso (`members.full_name_public`)
 * → `Luca Bianchi`. È la regola di `nomeAccorciato`, con una lettera in più
 * quando serve (`lettere`): «Luca Bi.».
 *
 * ⚖️ Vale per quello che il ponte manda **sugli altri**: la rosa di una
 * partita, la rubrica, gli avvisi su chi entra e chi esce, il nome di chi
 * invita. Il proprio nome per sé e tutto quello che vede la Segreteria restano
 * interi.
 */
export function nomePerGliAltri(nome, cognome, intero, lettere = 1) {
    const n = nome.trim();
    const c = cognome.trim();
    if (intero || n === '' || c === '' || lettere >= c.length)
        return `${n} ${c}`.trim();
    // ⚠️ Lettere vere: «De Luca» con due lettere è «De.», mai «De .».
    const pezzo = c.slice(0, lettere).trimEnd();
    return `${n} ${(pezzo[0] ?? '').toUpperCase()}${pezzo.slice(1)}.`;
}
/**
 * ⭐ **Due nomi uguali nella stessa schermata** (scelta 2A): due lettere del
 * cognome, «Luca Bi.» e «Luca Br.», e **non di più** (mockup «Privacy del
 * nome», *«1A»*): chi resta uguale con due lettere resta uguale («Luca Bi.» e
 * «Luca Bi.»). Il cognome intero si vede solo col consenso, mai per
 * distinguere due persone; e mai le lettere di tutto il cognome («Luca Re»
 * resta «Luca R.»).
 *
 * ⚠️ Il confronto è su ciò che si VEDE (maiuscole e spazi), come in
 * `accorciaElenco`; la stessa persona due volte non è un'omonimia.
 */
/** Due lettere al massimo, e sempre almeno una in meno del cognome intero. */
function tettoDelleLettere(cognome) {
    return Math.max(1, Math.min(2, cognome.trim().length - 1));
}
export function nomiPerGliAltri(voci) {
    const lettere = voci.map(() => 1);
    const scritto = (i) => {
        const v = voci[i];
        return nomePerGliAltri(v.nome, v.cognome, v.intero, lettere[i]);
    };
    for (let giro = 0; giro < 40; giro++) {
        const chiPerScritta = new Map();
        voci.forEach((v, i) => {
            const chiave = scritto(i).replace(/\s+/g, ' ').toLowerCase();
            const insieme = chiPerScritta.get(chiave) ?? new Set();
            insieme.add(v.chi);
            chiPerScritta.set(chiave, insieme);
        });
        let cambiato = false;
        voci.forEach((v, i) => {
            const chiave = scritto(i).replace(/\s+/g, ' ').toLowerCase();
            const quanti = chiPerScritta.get(chiave)?.size ?? 0;
            if (quanti > 1 && !v.intero && lettere[i] < tettoDelleLettere(v.cognome)) {
                lettere[i] = lettere[i] + 1;
                cambiato = true;
            }
        });
        if (!cambiato)
            break;
    }
    return voci.map((_, i) => scritto(i));
}
//# sourceMappingURL=soci.js.map